import React from 'react'
import { motion } from 'framer-motion'
import { Link } from 'react-router-dom'
import { ArrowRight, Home, Wrench, Users, Phone, FileText } from 'lucide-react'

const Sitemap = () => {
  const currentYear = new Date().getFullYear()

  const mainPages = [
    {
      title: 'Главная',
      path: '/',
      description: 'Главная страница сайта Антикор Авто Брест',
      icon: Home
    },
    {
      title: 'Услуги',
      path: '/services',
      description: 'Полный спектр услуг по антикоррозийной обработке автомобилей',
      icon: Wrench
    },
    {
      title: 'О нас',
      path: '/about',
      description: 'Информация о компании, нашей команде и достижениях',
      icon: Users
    },
    {
      title: 'Контакты',
      path: '/contact',
      description: 'Контактная информация и часто задаваемые вопросы',
      icon: Phone
    }
  ]

  const servicePages = [
    {
      title: 'Обработка днища автомобиля',
      path: '/services#bottom',
      description: 'Комплексная защита днища от коррозии'
    },
    {
      title: 'Защита кузова автомобиля',
      path: '/services#body',
      description: 'Защита внешних поверхностей кузова'
    },
    {
      title: 'Обработка скрытых полостей',
      path: '/services#cavities',
      description: 'Защита внутренних полостей кузова'
    },
    {
      title: 'Комплексная антикоррозийная обработка',
      path: '/services#complex',
      description: 'Полная защита всего автомобиля'
    }
  ]

  const infoPages = [
    {
      title: 'Карта сайта',
      path: '/sitemap',
      description: 'Структура и навигация по сайту'
    }
  ]

  const contactInfo = {
    phone: '+375 (33) 666-51-22',
    address: 'г. Брест, ул. Мощенского, 90',
    workingHours: 'Пн-Пт: 9:00-18:00, Сб: 9:00-15:00'
  }

  return (
    <div className="page-padding">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-primary-900 via-primary-800 to-secondary-900 text-white py-20">
        <div className="container-custom text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="flex items-center justify-center mb-6"
          >
            <FileText className="w-12 h-12 mr-4" />
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold">
              Карта сайта
            </h1>
          </motion.div>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="text-xl md:text-2xl text-white/90 max-w-3xl mx-auto leading-relaxed"
          >
            Навигация по всем разделам сайта Антикор Авто Брест
          </motion.p>
        </div>
      </section>

      {/* Main Pages */}
      <section className="section-padding">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-secondary-900 mb-4">
              Основные страницы
            </h2>
            <p className="text-lg text-secondary-600 max-w-3xl mx-auto">
              Главные разделы нашего сайта с подробным описанием
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {mainPages.map((page, index) => (
              <motion.div
                key={page.path}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="group"
              >
                <div className="card h-full hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2">
                  <div className="flex items-center mb-4">
                    <div className="w-12 h-12 bg-primary-100 rounded-full flex items-center justify-center mr-4 group-hover:bg-primary-200 transition-colors duration-200">
                      <page.icon className="w-6 h-6 text-primary-600" />
                    </div>
                    <h3 className="text-xl font-semibold text-secondary-900 group-hover:text-primary-600 transition-colors duration-200">
                      {page.title}
                    </h3>
                  </div>
                  <p className="text-secondary-600 mb-4 leading-relaxed">
                    {page.description}
                  </p>
                  <Link
                    to={page.path}
                    className="inline-flex items-center text-primary-600 hover:text-primary-700 font-medium transition-colors duration-200"
                  >
                    Перейти к странице
                    <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform duration-200" />
                  </Link>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Service Pages */}
      <section className="section-padding bg-secondary-50">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-secondary-900 mb-4">
              Наши услуги
            </h2>
            <p className="text-lg text-secondary-600 max-w-3xl mx-auto">
              Детальная информация о всех видах антикоррозийной обработки
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {servicePages.map((service, index) => (
              <motion.div
                key={service.path}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="group"
              >
                <div className="bg-white rounded-lg p-6 shadow-md hover:shadow-lg transition-all duration-300 border-l-4 border-primary-500">
                  <h3 className="text-lg font-semibold text-secondary-900 mb-2 group-hover:text-primary-600 transition-colors duration-200">
                    {service.title}
                  </h3>
                  <p className="text-secondary-600 mb-4">
                    {service.description}
                  </p>
                  <Link
                    to={service.path}
                    className="inline-flex items-center text-primary-600 hover:text-primary-700 font-medium transition-colors duration-200"
                  >
                    Подробнее
                    <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform duration-200" />
                  </Link>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Information */}
      <section className="section-padding">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-secondary-900 mb-4">
              Контактная информация
            </h2>
            <p className="text-lg text-secondary-600 max-w-3xl mx-auto">
              Свяжитесь с нами любым удобным способом
            </p>
          </motion.div>

          <div className="bg-gradient-to-r from-primary-600 to-accent-600 rounded-2xl p-8 md:p-12 text-white">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              <div className="text-center">
                <Phone className="w-8 h-8 mx-auto mb-4 text-accent-300" />
                <h3 className="text-lg font-semibold mb-2">Телефон</h3>
                <p className="text-accent-200">{contactInfo.phone}</p>
              </div>
              <div className="text-center">
                <Home className="w-8 h-8 mx-auto mb-4 text-accent-300" />
                <h3 className="text-lg font-semibold mb-2">Адрес</h3>
                <p className="text-accent-200">{contactInfo.address}</p>
              </div>
              <div className="text-center">
                <Wrench className="w-8 h-8 mx-auto mb-4 text-accent-300" />
                <h3 className="text-lg font-semibold mb-2">Режим работы</h3>
                <p className="text-accent-200">{contactInfo.workingHours}</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Info Pages */}
      <section className="section-padding bg-secondary-50">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-secondary-900 mb-4">
              Дополнительная информация
            </h2>
            <p className="text-lg text-secondary-600 max-w-3xl mx-auto">
              Полезные страницы и документы
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {infoPages.map((page, index) => (
              <motion.div
                key={page.path}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="group"
              >
                <div className="bg-white rounded-lg p-6 shadow-md hover:shadow-lg transition-all duration-300 text-center">
                  <h3 className="text-lg font-semibold text-secondary-900 mb-2 group-hover:text-primary-600 transition-colors duration-200">
                    {page.title}
                  </h3>
                  <p className="text-secondary-600 mb-4 text-sm">
                    {page.description}
                  </p>
                  <Link
                    to={page.path}
                    className="inline-flex items-center text-primary-600 hover:text-primary-700 font-medium transition-colors duration-200"
                  >
                    Открыть
                    <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform duration-200" />
                  </Link>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Footer Info */}
      <section className="section-padding">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <div className="bg-gradient-to-r from-primary-50 to-accent-50 rounded-2xl p-8">
              <h3 className="text-2xl font-bold text-secondary-900 mb-4">
                Антикор Авто Брест
              </h3>
              <p className="text-lg text-secondary-600 mb-4">
                Профессиональная антикоррозийная обработка автомобилей в Бресте
              </p>
              <p className="text-sm text-secondary-500">
                © {currentYear} Антикор Авто Брест. УНП 291857843. Все права защищены.
              </p>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  )
}

export default Sitemap
